import { PARIS } from '@utils/map/map.constants';
import { useReducer } from 'react';
import { ViewState } from 'react-map-gl/mapbox';

import { Coordinates } from '@/types/commons/commons.types';
import { ViewMode } from '@/types/map.types';

interface State {
  viewState: ViewState;
  viewMode: ViewMode;
  isNorthLocked: boolean;
}

type MapViewStateAction =
  | { type: 'UPDATE_VIEW'; view: ViewState }
  | { type: 'UPDATE_ZOOM'; zoom: number }
  | { type: 'SET_VIEW_MODE'; viewMode: ViewMode }
  | { type: 'SET_IS_NORTH_LOCKED'; isNorthLocked: boolean };

const DEFAULT_MAP_VIEW_STATE: ViewState = {
  longitude: 2.360508,
  latitude: 48.742108,
  zoom: 13, // [0, 22]
  bearing: 0,
  pitch: 0, // [0, 85]
  padding: {
    top: 0,
    bottom: 0,
    left: 0,
    right: 0,
  },
};
const initialState: State = {
  viewState: DEFAULT_MAP_VIEW_STATE,
  viewMode: '2D',
  isNorthLocked: true,
};

function mapViewStateReducer(state: State, action: MapViewStateAction): State {
  switch (action.type) {
    case 'UPDATE_VIEW':
      return { ...state, viewState: action.view };
    case 'UPDATE_ZOOM':
      return { ...state, viewState: { ...state.viewState, zoom: action.zoom } };
    case 'SET_VIEW_MODE':
      return { ...state, viewMode: action.viewMode };
    case 'SET_IS_NORTH_LOCKED':
      return { ...state, isNorthLocked: action.isNorthLocked };
    default:
      return state;
  }
}

export interface UseMapViewStateReturn {
  state: State;
  setViewState: (view: ViewState) => void;
  setZoom: (zoom: number) => void;
  setViewMode: (viewMode: ViewMode) => void;
  setIsNorthLocked: (isNorthLocked: boolean) => void;
}

export const useMapViewState = (center?: Coordinates | null): UseMapViewStateReturn => {
  const [state, dispatch] = useReducer(mapViewStateReducer, {
    ...initialState,
    viewState: {
      ...initialState.viewState,
      longitude: center?.longitude ?? PARIS.longitude,
      latitude: center?.latitude ?? PARIS.latitude,
    },
  });

  const setViewState = (view: ViewState) => {
    dispatch({ type: 'UPDATE_VIEW', view });
  };

  const setZoom = (zoom: number) => {
    dispatch({ type: 'UPDATE_ZOOM', zoom });
  };

  const setViewMode = (viewMode: ViewMode) => {
    dispatch({ type: 'SET_VIEW_MODE', viewMode });
  };

  const setIsNorthLocked = (isNorthLocked: boolean) => {
    dispatch({ type: 'SET_IS_NORTH_LOCKED', isNorthLocked });
  };

  return { state, setViewState, setZoom, setViewMode, setIsNorthLocked };
};
